/* eslint-disable eslint-comments/disable-enable-pair */

import { isDefined } from "@clipboard-health/util-ts";
// TODO: Use our own Card, Pill instead of Chip
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useCreateWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useCreateWorkerFavoritePlacement";
import { useDeleteWorkerFavoritePlacement } from "@src/appV2/redesign/Placements/api/useDeleteWorkerFavoritePlacement";
import { getCardType } from "@src/appV2/redesign/Placements/utils/card-type";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { usePlacementsFilter } from "../usePlacementsFilter";
import { showApplicationsStorageKey } from "../utils";
import { PlacementCardV2 } from "./PlacementCard/PlacementCardV2";
import { type PlacementCardProps } from "./PlacementCard/types";

export function PlacementCard(props: PlacementCardProps) {
  const { placement, placementCandidateId, pageIndex, placementIndex, source, minMaxPayEstimate } =
    props;

  const [filter] = usePlacementsFilter();
  const [showApplications] = useLocalStorage<boolean>(showApplicationsStorageKey, false);
  const worker = useDefinedWorker();

  const { mutateAsync: favoritePlacement, isLoading: isFavoritingPlacement } =
    useCreateWorkerFavoritePlacement();
  const { mutateAsync: unfavoritePlacement, isLoading: isUnfavoritingPlacement } =
    useDeleteWorkerFavoritePlacement();

  return (
    <PlacementCardV2
      {...props}
      ongoingFavoritingRequest={isFavoritingPlacement || isUnfavoritingPlacement}
      workerQualifications={new Set(worker.licensesData.map((license) => license.qualification))}
      onFavoriteToggle={async (newFavoriteStatus) => {
        if (isFavoritingPlacement || isUnfavoritingPlacement) {
          return;
        }

        if (newFavoriteStatus) {
          await favoritePlacement({
            workerId: worker.userId,
            placementId: placement.id,
            source,
          });
        } else if (isDefined(placement.workerFavoritePlacementId)) {
          await unfavoritePlacement({
            workerId: worker.userId,
            workerFavoritePlacementId: placement.workerFavoritePlacementId,
            placementId: placement.id,
            source,
          });
        }
      }}
      onClick={() => {
        logEvent(APP_V2_APP_EVENTS.PLACEMENT_DETAILS_PAGE_VIEWED, {
          placementCandidateId,
          placementDetails: placement,
          page: pageIndex + 1,
          listIndex: placementIndex + 1,
          source,
          cardType: getCardType(placement),
          matchedType: placement.matchedType,
          filter,
          showApplications,
          isRecruiterScreeningEnabled: placement.isRecruiterScreeningEnabled ?? false,
          hiringEvents: placement.hiringEvents?.map((event) => {
            return {
              id: event.id,
              name: event.type,
              startDate: event.fixedSchedule?.startDate,
              endDate: event.fixedSchedule?.endDate,
            };
          }),
        });
      }}
      onInView={() => {
        logEvent(APP_V2_APP_EVENTS.PLACEMENT_CARD_VIEWED, {
          placementCandidateId,
          placement,
          page: pageIndex + 1,
          listIndex: placementIndex + 1,
          source,
          matchedType: placement.matchedType,
          filter,
          showApplications,
        });
      }}
      onFooterInView={() => {
        logEvent(APP_V2_APP_EVENTS.PLACEMENT_PAY_VIEWED, {
          workerId: worker.userId,
          placementCandidateId,
          workplaceId: placement.workplace.id,
          placementId: placement.id,
          matchedType: placement.matchedType,
          payRateExperiment: true,
          payRate: minMaxPayEstimate
            ? {
                min: minMaxPayEstimate.min,
                max: minMaxPayEstimate.max,
              }
            : {
                min: placement.payRange?.minPay?.amountInMinorUnits,
                max: placement.payRange?.maxPay?.amountInMinorUnits,
              },
        });
      }}
    />
  );
}
